import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Response,
  SelectItemProps,
  PermissionEnum,
  Currency,
  Country,
} from '@ui/src';
import { PaymentType, RiskScoreRange } from './types';
import { configurationDictionariesApi, PermissionReponse } from './service';

export interface ConfigurationsDictionariesState {
  currencies?: SelectItemProps[];
  accounts?: SelectItemProps[];
  countries?: SelectItemProps[];
  activePermissions?: PermissionEnum[];
  riskScoreRanges?: RiskScoreRange[];
  baseCurrency?: Currency;
  currenciesList?: Currency[];
  paymentTypes?: PaymentType[];
  overdraftEnabled?: boolean;
  whitelabel?: {
    data: {
      value: Record<string, string>;
    };
  };
  transactionTypes?: { id: string; name: string }[];
}

const initialState: ConfigurationsDictionariesState = {
  countries: [],
  accounts: [],
  currencies: [],
};

export const configurationDictionariesSlice = createSlice({
  name: 'configurationDictionaries',
  initialState,
  reducers: {
    setActivePermissions: (state, action: PayloadAction<PermissionEnum[]>) => {
      state.activePermissions = action.payload;
    },
    setPaymentTypes: (state, action: PayloadAction<PaymentType[]>) => {
      state.paymentTypes = action.payload?.filter((item) => item.enabled);
    },
    setConfigurationCurrencies: (state, action: PayloadAction<Currency[]>) => {
      state.currencies = action.payload.map((item) => ({
        name: item.description,
        value: item.id,
        suffix: item.name,
        description: item?.code?.toString(),
      }));

      state.currenciesList = action.payload.map((item) => item);
    },
    setConfigurationSettings: (
      state,
      action: PayloadAction<{ key: string; data: { value: boolean } }[]>,
    ) => {
      const overdraftSetting = action.payload?.find(
        (item) => item.key === 'OVERDRAFT-ENABLED',
      );

      if (overdraftSetting) {
        if (
          overdraftSetting.data.value === undefined ||
          overdraftSetting.data.value === null
        ) {
          state.overdraftEnabled = false;
        } else {
          state.overdraftEnabled = overdraftSetting.data.value;
        }
      }
    },
    setConfigurationCountries: (state, action: PayloadAction<Country[]>) => {
      state.countries = action.payload.map((item) => ({
        name: item.name,
        value: item.id,
      }));
    },
    setConfigurationBaseCurrency: (state, action: PayloadAction<Currency>) => {
      state.baseCurrency = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      configurationDictionariesApi.endpoints.getConfigurationBaseCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: Currency }>) => {
        state.baseCurrency = action.payload.data;
      },
    );
    builder.addMatcher(
      configurationDictionariesApi.endpoints
        .getConfigurationDictionariesTransactionTypes.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<{ id: string; name: string }>>,
      ) => {
        state.transactionTypes = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      configurationDictionariesApi.endpoints.getConfigurationSettings
        .matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{ key: string; data: { value: boolean } }>
        >,
      ) => {
        const overdraftSetting = action.payload.data.edges?.find(
          (item) => item.node.key === 'OVERDRAFT-ENABLED',
        );

        if (overdraftSetting) {
          if (
            overdraftSetting.node.data.value === undefined ||
            overdraftSetting.node.data.value === null
          ) {
            state.overdraftEnabled = false;
          } else {
            state.overdraftEnabled = overdraftSetting.node.data.value;
          }
        }
      },
    );
    builder.addMatcher(
      configurationDictionariesApi.endpoints.getConfigurationCountries
        .matchFulfilled,
      (state, action: PayloadAction<Response<Country>>) => {
        state.countries = action.payload.data.edges.map((item) => ({
          name: item.node.name,
          value: item.node.id,
        }));
      },
    );
    builder.addMatcher(
      configurationDictionariesApi.endpoints
        .getDictionariesConfigurationCurrencies.matchFulfilled,
      (state, action: PayloadAction<Response<Currency>>) => {
        state.currencies = action.payload.data.edges.map((item) => ({
          name: item.node.description,
          value: item.node.id,
          suffix: item.node.name,
        }));

        state.currenciesList = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      configurationDictionariesApi.endpoints.getConfigurationDictionaries
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{
          riskScoreRanges: Response<RiskScoreRange>;
        }>,
      ) => {
        if (action.payload.riskScoreRanges.data.edges) {
          state.riskScoreRanges = action.payload.riskScoreRanges.data.edges
            .map((item) => item.node)
            .sort((a, b) => a.rangeStart - b.rangeStart);
        }
      },
    );
    builder.addMatcher(
      configurationDictionariesApi.endpoints.getConfigurationPermissions
        .matchFulfilled,
      (state, action: PayloadAction<{ data: PermissionReponse }>) => {
        state.activePermissions = action.payload.data.permissions;

        localStorage.setItem(
          'activePermissions',
          JSON.stringify(action.payload.data.permissions),
        );
      },
    );
    builder
      .addMatcher(
        configurationDictionariesApi.endpoints.getConfigurationPaymentTypes
          .matchFulfilled,
        (state, action: PayloadAction<Response<PaymentType>>) => {
          state.paymentTypes = action?.payload?.data?.edges
            ?.filter((item) => item.node.enabled)
            ?.map((item) => item.node);
        },
      )
      .addMatcher(
        configurationDictionariesApi.endpoints.getConfigurationWhitelabel
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              data: {
                value: Record<string, string>;
              };
            };
          }>,
        ) => {
          state.whitelabel = action.payload.data;
        },
      );
  },
});

export const {
  setActivePermissions,
  setPaymentTypes,
  setConfigurationBaseCurrency,
  setConfigurationCountries,
  setConfigurationCurrencies,
  setConfigurationSettings,
} = configurationDictionariesSlice.actions;

export default configurationDictionariesSlice.reducer;
